'use client';
import * as React from 'react';
import { Button, Icon } from '@/components/site/ui';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * One line of explanation on hover/focus, for a control whose consequence is not obvious
 * (a decision that moves money or an agenda).
 *
 * This is `HoverCard` — the sanctioned hover-peek — narrowed to a label. **There is no Tooltip
 * primitive in this app on purpose**: a whole extra Radix dependency for one line of text was
 * refused, and every hint in the product goes through here instead. Do not add one.
 *
 * `label` is both the visible text and the trigger's accessible name, from a single i18n key,
 * so what the user reads and what a screen reader says cannot drift.
 */
export function HintHover({
  label,
  children,
  side,
  align = 'center',
  contentClassName,
  interaction = 'hover',
}: {
  /** Translated hint. Also the trigger's accessible name. */
  label: string;
  /** Custom trigger (must forward ref + accept aria-label). Omit for the default info icon. */
  children?: React.ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  contentClassName?: string;
  /**
   * `'click'` for a hint that lives inside a dialog. A hover card opens on FOCUS
   * too, and a dialog moves focus into its content on open — so a hover hint near
   * the top of a modal pops open by itself before the user has done anything.
   * Click keeps it shut until asked.
   */
  interaction?: 'hover' | 'click';
}) {
  const trigger =
    children ?? (
      <Button
        variant="ghost"
        size="icon"
        aria-label={label}
        className="h-5 w-5 rounded-md text-muted-foreground hover:text-foreground"
      >
        <Icon name="information-circle" size={14} />
      </Button>
    );
  const body = <p className="text-[0.78125rem] leading-snug text-foreground">{label}</p>;
  const contentClass = cn('w-auto max-w-[17rem] px-3 py-2', contentClassName);

  if (interaction === 'click') {
    return (
      <Popover>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent side={side} align={align} className={contentClass}>
          {body}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>{trigger}</HoverCardTrigger>
      <HoverCardContent side={side} align={align} className={contentClass}>
        {body}
      </HoverCardContent>
    </HoverCard>
  );
}
